import type { TimeEntry, NewTimeEntry, Project, BankHoliday, ISODateString, PeriodMonth } from '../types/timesheet.types';
import { ProjectType } from '../types/timesheet.types';
import { isWeekend, parseISODate, isDateInMonth } from './dateUtils';
import { VALIDATION, BANK_HOLIDAY_PROJECT_CODE } from './constants';

export interface CellData {
  date: ISODateString;
  hours: number | null;
  entryId: string | undefined;
  isWeekend: boolean;
  isOOM: boolean;
  isBH: boolean;
  isReadOnly: boolean;
  isOverriddenBH?: boolean;
}

export interface GridRow {
  projectCode: string;
  projectName: string;
  projectType: ProjectType | null;
  isBHRow: boolean;
  bankHolidayName?: string;
  cells: CellData[];
  rowTotal: number;
}

/**
 * Transforms flat time entries + metadata into display rows for the weekly grid.
 * Separates BH auto-row from project rows and resolves per-cell edit constraints.
 */
export function buildGridRows(
  entries: readonly (TimeEntry | NewTimeEntry)[],
  projects: readonly Project[],
  bankHolidays: readonly BankHoliday[],
  weekDays: readonly ISODateString[],
  periodMonth: PeriodMonth,
  userAddedCodes: readonly string[],
  isSheetReadOnly: boolean,
  overriddenBHDates?: ReadonlySet<ISODateString>,
): { rows: GridRow[]; bhRow: GridRow | null } {
  const projectMap = new Map(projects.map(p => [p.projectCode, p]));

  // Bank holidays in this week: date → name
  const bhInWeek = new Map<ISODateString, string>();
  for (const bh of bankHolidays) {
    if ((weekDays as readonly string[]).includes(bh.holidayDate)) {
      bhInWeek.set(bh.holidayDate, bh.holidayName);
    }
  }

  // Project entries only (no BH auto-entries)
  const projectEntries = entries.filter(e => e.projectCode !== BANK_HOLIDAY_PROJECT_CODE);

  // Unique codes: entries visible in current week + user-added rows
  const codesFromEntries = [
    ...new Set(
      projectEntries
        .filter(e => (weekDays as readonly string[]).includes(e.dateWorked))
        .map(e => e.projectCode),
    ),
  ];
  const allCodes = [...new Set([...codesFromEntries, ...userAddedCodes])];

  const rows: GridRow[] = allCodes.map(code => {
    const project = projectMap.get(code);
    const cells: CellData[] = weekDays.map(date => {
      const entry = projectEntries.find(e => e.projectCode === code && e.dateWorked === date);
      const oom = !isDateInMonth(date, periodMonth);
      const weekend = isWeekend(parseISODate(date));
      const bh = bhInWeek.has(date);
      const isOverriddenBH = bh && (overriddenBHDates?.has(date) ?? false);
      const hasExistingHours = bh && !!entry && (entry.hours ?? 0) > 0;
      const beforeStart = project?.startDate ? date < project.startDate : false;
      const afterEnd = project?.endDate ? date > project.endDate : false;
      return {
        date,
        hours: entry && entry.hours ? entry.hours : null,
        entryId: entry?.id,
        isWeekend: weekend,
        isOOM: oom,
        isBH: bh,
        isOverriddenBH: isOverriddenBH || hasExistingHours,
        isReadOnly: isSheetReadOnly || oom || (bh && !isOverriddenBH && !hasExistingHours) || beforeStart || afterEnd,
      };
    });
    return {
      projectCode: code,
      projectName: project?.projectName ?? code,
      projectType: project?.type ?? null,
      isBHRow: false,
      cells,
      rowTotal: cells.reduce((s, c) => s + (c.hours ?? 0), 0),
    };
  });

  // Bank holiday auto-row (one row for all BH days in the week)
  let bhRow: GridRow | null = null;
  if (bhInWeek.size > 0) {
    const cells: CellData[] = weekDays.map(date => {
      const isBH = bhInWeek.has(date);
      const isOverriddenBH = isBH && (overriddenBHDates?.has(date) ?? false);
      let hours: number | null = null;
      if (isBH) {
        const projHours = projectEntries
          .filter(e => e.dateWorked === date)
          .reduce((s, e) => s + e.hours, 0);
        hours = Math.max(0, VALIDATION.BANK_HOLIDAY_DEFAULT_HOURS - projHours);
      }
      return {
        date,
        hours,
        entryId: undefined,
        isWeekend: isWeekend(parseISODate(date)),
        isOOM: !isDateInMonth(date, periodMonth),
        isBH,
        isReadOnly: true,
        isOverriddenBH,
      };
    });
    const names = [...bhInWeek.values()].join(', ');
    bhRow = {
      projectCode: BANK_HOLIDAY_PROJECT_CODE,
      projectName: names,
      projectType: null,
      isBHRow: true,
      bankHolidayName: names,
      cells,
      rowTotal: cells.reduce((s, c) => s + (c.hours ?? 0), 0),
    };
  }

  return { rows, bhRow };
}
